import axios from "axios";
import { getStoredToken } from "../utils/tokenUtils";
import { installApiLoadingInterceptors } from "../utils/apiLoading";

const uploadHttp = axios.create();
installApiLoadingInterceptors(uploadHttp);

/**
 * Extract base64 images from HTML content and convert to File objects
 * @param {string} html - HTML content with base64 images
 * @returns {Array} Array of { src, data, mimeType, fileName }
 */
export const extractBase64Images = (html) => {
  if (!html) return [];

  const images = [];
  const imgRegex = /<img[^>]+src="data:([^;]+);base64,([^"]+)"/gi;
  let match;

  while ((match = imgRegex.exec(html)) !== null) {
    const mimeType = match[1];
    const base64Data = match[2];
    const originalSrc = match[0];

    // Determine file extension from mime type
    const extension = mimeType.split("/")[1] || "png";
    const fileName = `image_${Date.now()}_${Math.random().toString(36).substring(7)}.${extension}`;

    images.push({
      src: originalSrc,
      data: base64Data,
      mimeType,
      fileName,
      fullDataUrl: `data:${mimeType};base64,${base64Data}`,
    });
  }

  return images;
};

/**
 * Convert base64 data to File object
 * @param {string} base64Data - Base64 encoded image data
 * @param {string} fileName - File name
 * @param {string} mimeType - MIME type
 * @returns {File}
 */
export const base64ToFile = (base64Data, fileName, mimeType) => {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new File([byteArray], fileName, { type: mimeType });
};

/**
 * Replace base64 image URLs with Supabase URLs in HTML content
 * @param {string} html - HTML content
 * @param {Array} replacements - Array of { originalSrc, newUrl, fullDataUrl }
 * @returns {string} Updated HTML
 */
export const replaceImageUrls = (html, replacements) => {
  if (!html || !replacements || replacements.length === 0) return html;

  let updatedHtml = html;
  replacements.forEach(({ originalSrc, newUrl, fullDataUrl }) => {
    if (!fullDataUrl) return;

    // Use simple string replacement instead of regex for large base64 data URLs
    // This avoids "Regular expression too large" errors with large images
    // Replace the full data URL directly using string methods
    if (updatedHtml.includes(fullDataUrl)) {
      updatedHtml = updatedHtml.split(fullDataUrl).join(newUrl);
    }

    // Also handle src="..." and src='...' patterns with string replacement
    // Extract a unique prefix from the data URL to match without using the full base64 string
    const dataUrlPrefix = fullDataUrl.substring(
      0,
      Math.min(100, fullDataUrl.indexOf(",") + 1),
    );
    if (dataUrlPrefix && updatedHtml.includes(dataUrlPrefix)) {
      // Use a more targeted approach: find img tags and replace src attributes
      const imgTagRegex = /<img[^>]+>/gi;
      updatedHtml = updatedHtml.replace(imgTagRegex, (imgTag) => {
        if (imgTag.includes(fullDataUrl) || imgTag.includes(dataUrlPrefix)) {
          // Replace the src attribute with the new URL
          return imgTag.replace(/src=["']([^"']*)["']/gi, (match, srcValue) => {
            if (
              srcValue === fullDataUrl ||
              srcValue.startsWith(dataUrlPrefix)
            ) {
              return `src="${newUrl}"`;
            }
            return match;
          });
        }
        return imgTag;
      });
    }
  });
  return updatedHtml;
};

/**
 * S3 Upload Service
 * Handles file uploads to S3 bucket via backend API
 */
class S3UploadService {
  constructor() {
    this.s3BaseUrl = process.env.REACT_APP_S3_URL || "";
  }

  /**
   * Upload file to S3 via backend API
   * @param {File} file - File to upload
   * @param {string} folder - Optional folder path in S3 (e.g., 'test-cases', 'test-steps')
   * @param {string} clientId - Client ID for organizing files
   * @param {string} projectId - Optional project ID for project-specific attachments
   * @returns {Promise<string>} - URL of uploaded file
   */
  async uploadFile(
    file,
    folder = "test-cases",
    clientId = null,
    projectId = null,
  ) {
    try {
      // Always use backend API endpoint for file uploads
      // Backend handles S3 upload with proper credentials and security
      const uploadUrl =
        process.env.REACT_APP_API_URL || "http://localhost:3000";

      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder", folder);
      if (clientId) {
        formData.append("client_id", clientId);
      }
      if (projectId) {
        formData.append("project_id", projectId);
      }

      // Get auth token for upload request
      const token = getStoredToken();
      const headers = {
        "Content-Type": "multipart/form-data",
      };

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      try {
        const response = await uploadHttp.post(
          `${uploadUrl}/api/upload`,
          formData,
          {
            headers: headers,
          },
        );

        // Handle different response formats
        if (response.data) {
          // If response has data.url (successResponse format)
          if (response.data.data && response.data.data.url) {
            return response.data.data.url;
          }
          // If response has url directly (direct format)
          if (response.data.url) {
            return response.data.url;
          }
          // If response has data directly
          if (response.data.data) {
            return response.data.data;
          }
        }

        throw new Error("Upload failed: No URL returned in response");
      } catch (backendError) {
        console.error("Backend upload error:", backendError);

        // If backend upload fails and S3 URL is configured, try direct S3 upload as fallback
        if (this.s3BaseUrl) {
          console.warn(
            "Backend upload failed. Trying direct S3 upload as fallback.",
          );

          const s3FormData = new FormData();
          s3FormData.append("file", file);
          s3FormData.append("folder", folder);

          const endpoint = `${this.s3BaseUrl}${this.s3BaseUrl.endsWith("/") ? "" : "/"}upload`;

          try {
            const s3Response = await uploadHttp.post(endpoint, s3FormData, {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            });

            if (s3Response.data && s3Response.data.url) {
              return s3Response.data.url;
            }
          } catch (s3Error) {
            console.error("Direct S3 upload also failed:", s3Error);
            throw new Error(
              `Both backend and direct S3 upload failed. Backend error: ${backendError.message}`,
            );
          }
        }

        // If all upload methods fail, throw error
        throw new Error(
          `Upload failed: ${backendError.message || "Unknown error"}`,
        );
      }
    } catch (error) {
      console.error("File upload error:", error);
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  }

  /**
   * Upload multiple files to S3
   * @param {File[]} files - Array of files to upload
   * @param {string} folder - Optional folder path in S3
   * @param {string} clientId - Client ID for organizing files
   * @param {string} projectId - Optional project ID for project-specific attachments
   * @returns {Promise<string[]>} - Array of URLs of uploaded files
   */
  async uploadFiles(
    files,
    folder = "test-cases",
    clientId = null,
    projectId = null,
  ) {
    try {
      const uploadPromises = files.map((file) =>
        this.uploadFile(file, folder, clientId, projectId),
      );
      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Multiple file upload error:", error);
      throw error;
    }
  }

  /**
   * Delete file from S3 via backend API
   * @param {string} fileUrl - URL of file to delete
   * @returns {Promise<void>}
   */
  async deleteFile(fileUrl) {
    try {
      const uploadUrl =
        process.env.REACT_APP_API_URL || "http://localhost:3000";
      const token = getStoredToken();

      const response = await uploadHttp.post(
        `${uploadUrl}/api/upload/delete`,
        { url: fileUrl },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("S3 delete error:", error);
      throw new Error(`Failed to delete file: ${error.message}`);
    }
  }
}

const s3UploadService = new S3UploadService();
export default s3UploadService;
